import { Component, OnInit, Input, ChangeDetectorRef } from '@angular/core';
import { fade, openClose } from '@core/animations/animations';
import {
	CapacityTypeLabels,
	ClientsPerCapacityLabels,
} from '@core/util/capacity';
import {
	Capacity,
	CapacityType,
	ClientsCapacity,
	CapacityPerClientType,
} from '@models/matter';
import { UntilDestroy, untilDestroyed } from '@ngneat/until-destroy';
import { ViewSelectSnapshot } from '@ngxs-labs/select-snapshot';
import { Select } from '@ngxs/store';
import { MatterClients } from '@state/global/matter_clients';
import { MatterDispatch } from '@state/matter';
import { SelectedMatter } from '@state/matter/matter.selectors';
import { cloneDeep } from 'lodash';
import { Observable } from 'rxjs';
import {
	DropdownOption,
	DropdownResult,
} from 'src/app/shared/atomic/atoms/dropdown-options/dropdown-options.component';
import { ICON } from '@core/util/icon';
import { TestHooks } from './capacity.hooks';

const CapacityOptions: DropdownOption<CapacityType>[] = [
	{ name: CapacityTypeLabels[CapacityType.SILENT], value: CapacityType.SILENT },
	{
		name: CapacityTypeLabels[CapacityType.REGISTERED_OWNER],
		value: CapacityType.REGISTERED_OWNER,
	},
];

const MultipleClientsCapacityOptions: DropdownOption<CapacityType>[] = [
	{
		name: CapacityTypeLabels[CapacityType.JOIN_TENANTS],
		value: CapacityType.JOIN_TENANTS,
	},
	{
		name: CapacityTypeLabels[CapacityType.TENANTS_IN_COMMON],
		value: CapacityType.TENANTS_IN_COMMON,
	},
	{
		name: `Add ${CapacityTypeLabels[CapacityType.CAPACITY_PER_CLIENT].toLowerCase()}`,
		value: CapacityType.CAPACITY_PER_CLIENT,
		icon: ICON.ADD,
	},
];

const CapacityTypesWithShares = [
	CapacityType.TENANTS_IN_COMMON,
	CapacityType.CAPACITY_PER_CLIENT,
];
const OtherCapacityOption = Object.values(CapacityPerClientType).map(
	(value) => ({
		name: ClientsPerCapacityLabels[value],
		value,
	}),
);

@UntilDestroy()
@Component({
	selector: 'app-party-capacity',
	templateUrl: './capacity.component.html',
	styleUrls: ['./capacity.component.scss'],
	animations: [openClose(), fade()],
	standalone: false,
})
export class PartyCapacityComponent implements OnInit {
	testHooks = TestHooks;
	public capacityOptions = CapacityOptions;
	public selectedCapacity: Capacity;
	public selectedCapacityString: string;
	public selectedOtherCapacityString: string;
	public clientsCapacity: ClientsCapacity;
	public capacityType = CapacityType;
	public ICON = ICON;
	public otherCapacityOptions: DropdownOption<CapacityPerClientType>[] =
		OtherCapacityOption;
	public capacityTypesWithShares = CapacityTypesWithShares;
	public showTotalError = false;
	@Input() label;
	@Input() showTitle;

	@Select(MatterClients.get_clientsCapacity)
	clientsCapacity$: Observable<ClientsCapacity>;
	@ViewSelectSnapshot(SelectedMatter.id) matterId: string;

	constructor(
		private matterDispatch: MatterDispatch,
		private cd: ChangeDetectorRef,
	) {}

	ngOnInit(): void {
		this.clientsCapacity$
			.pipe(untilDestroyed(this))
			.subscribe((_clientsCapacity) => {
				this.clientsCapacity = cloneDeep(_clientsCapacity);

				if (
					Object.keys(_clientsCapacity?.shares)?.length > 1 ||
					Object.keys(_clientsCapacity?.otherCapacity)?.length > 1
				) {
					this.capacityOptions = [
						...CapacityOptions,
						...MultipleClientsCapacityOptions,
					];
				} else {
					this.capacityOptions = CapacityOptions;
				}

				if (_clientsCapacity) {
					this.selectedCapacityString =
						CapacityTypeLabels[_clientsCapacity?.capacityType];
				}

				this.cd.detectChanges();
			});
	}

	trackShare = (index, item) => item.key;

	setCapacityType(capacityType: DropdownResult<DropdownOption<CapacityType>>) {
		this.clientsCapacity.capacityType = capacityType.result.value;

		if (this.capacityTypesWithShares.includes(capacityType.result.value)) {
			const keys = Object.keys(this.clientsCapacity.shares);
			keys.forEach((key) => {
				this.clientsCapacity.shares[key].share = 50;
			});
		} else {
			this.clientsCapacity.shares = {};
		}

		if (capacityType.result.value === CapacityType.CAPACITY_PER_CLIENT) {
			Object.keys(this.clientsCapacity.otherCapacity).forEach((key) => {
				this.clientsCapacity.otherCapacity[key].capacityType =
					CapacityPerClientType.SILENT;
			});
		} else {
			this.clientsCapacity.otherCapacity = {};
		}

		this.updateCapacity();
	}

	setShare(clientId, value) {
		const keys = Object.keys(this.clientsCapacity.shares);
		let _value = Number(value);
		if (_value < 0) {
			_value = 0;
			this.showTotalError = true;
		} else if (_value > 100) {
			_value = 100;
			this.showTotalError = true;
		} else {
			this.showTotalError = false;
		}
		this.clientsCapacity.shares[clientId].share = _value;
		if (
			this.clientsCapacity.capacityType === CapacityType.CAPACITY_PER_CLIENT
		) {
			this.clientsCapacity.otherCapacity[clientId].share = value;
		}
		if (keys.length === 2) {
			const otherKey = keys.filter((x) => x !== clientId);
			const otherAmount = 100 - Number(_value);
			if (otherKey && otherKey.length === 1) {
				this.clientsCapacity.shares[otherKey[0]].share = otherAmount;
			}
		}

		this.updateCapacity();
	}

	setOtherCapacityShare(clientId, value: string) {
		const _value = value;
		this.clientsCapacity.otherCapacity[clientId].share = _value;

		this.updateCapacity();
	}

	setOtherCapacity(
		clientId,
		capacityType: DropdownResult<DropdownOption<CapacityPerClientType>>,
	): void {
		this.clientsCapacity.otherCapacity[clientId].capacityType =
			capacityType.result.value;

		this.updateCapacity();
	}

	updateCapacity() {
		const shares = {};
		const otherCapacity = {};

		if (
			this.clientsCapacity.capacityType === CapacityType.CAPACITY_PER_CLIENT
		) {
			const data = Object.values(this.clientsCapacity.otherCapacity);
			data.reduce((prev, curr) => {
				shares[curr.client.id] = curr.share;
				otherCapacity[curr.client.id] = curr.capacityType;

				return prev;
			}, {});
		} else {
			const data = Object.values(this.clientsCapacity.shares);
			data.reduce((prev, curr) => {
				shares[curr.client.id] = curr.share;

				return prev;
			}, {});
		}

		const capacity: Capacity = {
			capacityType: this.clientsCapacity.capacityType ?? null,
			shares,
			otherCapacity,
		};

		this.matterDispatch.SetCapacity(this.matterId, capacity);
	}

	get total() {
		return Object.values(this.clientsCapacity?.shares)?.reduce(
			(prev, curr) => prev + Number(curr.share),
			0,
		);
	}
}
